import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { SquadIcon } from "components/Squads";
import { Col } from "components/Containers";
import { UserPhotoList } from "components/User";
import { colors, fontSize } from "style";
import { PrimaryButton } from "components/Buttons";
import Card from "components/Card";
import { Link } from "components/Links";
import { useAuth, useLinks, useMission, useSquad } from "hooks";
import { TextLoader } from "components/Loader";
import { shortenText } from "utils";
import { OrganizationLink } from "components/Organization";

/**
 * SquadCard
 *
 * @param {Object} data
 */
const SquadCard = ({ data }) => {
  const { isAdmin } = useAuth();
  const links = useLinks();
  const fetchSquadData = !data?.id || !data?.organization || !data?.assignments || data?.assignments?.length === 0;
  const { data: squadData } = useSquad({ id: data?.id, skip: !fetchSquadData });
  const { data: mission, loading } = useMission({ id: data?.missionId || squad?.missionId });
  const [squad, setSquad] = useState(data);

  useEffect(() => {
    if (squadData) {
      setSquad(squadData);
    }
  }, [squadData]);

  if (!squad) return null;

  return (
    <Card>
      <Col gap="2rem" css={styles.container}>
        <Col gap="0.5rem" css={styles.container}>
          <SquadIcon name={squad?.name} organizationName={squad?.organization?.name} size="8rem" />
          <div css={styles.title}>{squad?.name}</div>
          <div css={styles.subtitle}>
            <OrganizationLink data={squad?.organization} withOverlay={isAdmin} />
          </div>
          <div css={styles.description}>
            {loading && <TextLoader width={`20rem`} />}
            {shortenText(mission?.description, 120)}
          </div>
          <UserPhotoList
            users={squad?.assignments}
            thumbCount={squad?.assignments?.length}
            thumbLimit={5}
            thumbSize="3rem"
            thumbBorderRadius="100%"
          />
        </Col>
        <Link to={links.squad(squad?.slug)} css={styles.button} routerEnabled>
          <PrimaryButton css={styles.button}>
            <FormattedMessage id="Squads.SquadCard.cta" />
          </PrimaryButton>
        </Link>
      </Col>
    </Card>
  );
};

const styles = {
  container: css`
    align-items: center;
    text-align: center;
    line-height: normal;
  `,
  title: css`
    font-size: ${fontSize.medium};
    font-weight: 500;
  `,
  subtitle: css`
    font-size: ${fontSize.small};
  `,
  description: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyBase};
    margin: 1rem 0 0 0;
  `,
  button: css`
    display: block;
    width: 100%;
  `,
};

SquadCard.propTypes = {
  data: PropTypes.object.isRequired,
};

export default SquadCard;
